import { money } from "./tariff";
import type { Customer } from "./types";

/**
 * Reading a customer row. pay-ui spec §20.7.
 *
 * Small, and separate from the page for the reason every other `lib/` module
 * here is: what a figure *means* is worth a test, and a test that has to render a
 * table to ask the question is a test nobody writes.
 */

/** What a row shows when there is no figure to show. Never `NaN` beside real money. */
const NOT_A_FIGURE = "—";

/**
 * What this account has spent, or why there is no number.
 *
 * Three outcomes, and the middle one is the point. Minor units are cents in USD
 * and whole riel in KHR; there is no exchange rate anywhere in this system, so a
 * customer who has paid in both has no total — and the server sends `null` rather
 * than a sum with no unit. "Mixed" is the honest rendering of that: a figure here
 * that silently added 500 cents to 4,000 riel would be wrong in a way an operator
 * could not see.
 */
export function customerSpend(row: Customer): string {
  if (row.settledCount === 0) return NOT_A_FIGURE;
  if (row.settledMinor === null || row.settledCurrency === null) return "mixed";

  return money(row.settledMinor, row.settledCurrency);
}

/**
 * How an account's state is captioned.
 *
 * `disabled` is the panel's attention tone because it is the one state an
 * operator put the account into and may need to undo. `active` is `available` —
 * the palette's word for "nothing is happening to this", which is the truth about
 * almost every row on the page.
 */
export function customerStatusTone(status: string): "available" | "faulted" | "idle" {
  if (status === "disabled") return "faulted";
  if (status === "active") return "available";

  /**
   * A status this build does not know about. Drawn as idle rather than as an
   * alarm: a newer server having added one is not an incident, and colouring it
   * red would make every deploy order matter.
   */
  return "idle";
}
